import moment from "moment";

import { TemplateRef } from "@angular/core";

moment.updateLocale('en', {
  week: {
    dow: 1 // Monday is the start of the week
  }
});

export type DateSelectorType = 'single' | 'range';
export type FilterType = ContentHubPageFiltersType | SociuuhubPostListPageFiltersType | AutoFlowContentSelectionFiltersType;
export type DateIntervalType = { [key in DateIntervals]: string };

export enum ContentHubPageFiltersType {
  TAGS = 'tags',
  DATE = 'date',
  SEARCH = 'search',
  SOURCES = 'sources',
  NETWORKS = 'networks',
  COUNTRIES = 'countries',
  USERGROUPS = 'user_groups',
  SHOW_ARCHIVE = 'show_archive',
  CONTENT_VISIBILITY = 'content_visibility',
  WELCOME_EMAIL = 'welcome_email',
  EMAIL_VERIFICATION = 'email_verification',
  SOCIAL_CONNECTION = 'social_connection',
  REJECTION_CONTEXT = 'rejection_context',
  ROLE = 'role',
  USER_SOURCE = 'user_source',
}

export enum SociuuhubPostListPageFiltersType {
  DATE = 'date',
  STATUS = 'status',
  AUTHORS = 'authors',
  SHOW_ARCHIVE = 'show_archive',
  CAMPAIGN_TYPE = 'campaign_type',
}

export enum AutoFlowContentSelectionFiltersType {
  SOURCES = 'sources',
  SUGGESTED_SHARE_TEXT = 'suggested_share_text',
}

export enum FilterSectionType {
  RADIO = 'radioFilter',
  SEARCH = 'searchFilter',
  DATE_SELECTOR = 'dateSelector',
  SWITCH_TOGGLE = 'switchToggle',
  SELECT_DROPDOWN = 'selectDropdown',
}

export enum SelectDropdownType {
  TAG = 'tag',
  USER = 'user',
  SOURCE = 'source',
  CUSTOM = 'custom',
  COUNTRY = 'country',
  NETWORK = 'network',
  USERGROUP = 'usergroup',
  DEPARTMENT = 'department',
}

export enum DateIntervals {
  TODAY = 'Today',
  LAST_7_DAYS = 'Last 7 days',
  LAST_30_DAYS = 'Last 30 days',
  LAST_WEEK = 'Last week',
  THIS_WEEK = 'This week',
  LAST_MONTH = 'Last month',
  THIS_MONTH = 'This month',
  THIS_QUARTER = 'This quarter',
  LAST_QUARTER = 'Last quarter',
  LAST_TWO_QUARTER = 'Last two quarter',
  THIS_TWO_QUARTER = 'This two quarter',
  LAST_YEAR = 'Previous year',
  THIS_YEAR = 'Current year',
  ONE_YEAR = 'Last year',
  TOTAL = 'Total',
}

export const DATE_INTERVAL_MAPPING: DateIntervalType = {
  [DateIntervals.TODAY]: 'Today',
  [DateIntervals.LAST_7_DAYS]: 'Last 7 days',
  [DateIntervals.LAST_30_DAYS]: 'Last 30 days',
  [DateIntervals.LAST_WEEK]: 'Last week',
  [DateIntervals.THIS_WEEK]: 'This week',
  [DateIntervals.LAST_MONTH]: 'Last month',
  [DateIntervals.THIS_MONTH]: 'This month',
  [DateIntervals.THIS_QUARTER]: getQuarterType(DateIntervals.THIS_QUARTER),
  [DateIntervals.LAST_QUARTER]: getQuarterType(DateIntervals.LAST_QUARTER),
  [DateIntervals.LAST_TWO_QUARTER]: getQuarterType(DateIntervals.LAST_TWO_QUARTER),
  [DateIntervals.THIS_TWO_QUARTER]: getQuarterType(DateIntervals.THIS_TWO_QUARTER),
  [DateIntervals.LAST_YEAR]: 'Previous year',
  [DateIntervals.THIS_YEAR]: 'This year',
  [DateIntervals.ONE_YEAR]: 'One year',
  [DateIntervals.TOTAL]: 'Total',
};

function getQuarterType(quater: DateIntervals): string {
  switch (quater) {
    case DateIntervals.THIS_QUARTER: {
      return `This quarter (Q${getQuarterNumber(currentQuarterDateRange().dateFrom)})`;
    }
    case DateIntervals.LAST_QUARTER: {
      return `Previous quarter (Q${getQuarterNumber(lastQuarterDateRange().dateFrom)})`;
    }
    case DateIntervals.LAST_TWO_QUARTER: {
      return `Previous two quarters (Q${getQuarterNumber(lastTwoQuarterDateRange().dateFrom)} - Q${getQuarterNumber(lastTwoQuarterDateRange().dateTo)})`;
    }
    case DateIntervals.THIS_TWO_QUARTER: {
      return `This two quarter (Q${getQuarterNumber(currentTwoQuarterDateRange().dateFrom)} - Q${getQuarterNumber(currentTwoQuarterDateRange().dateTo)})`;
    }
  }``
}

function getQuarterNumber(date: string | Date): string {
  return moment(date).quarter().toString();
}

export interface IFilter {
  title: string;
  type: FilterType;
  sections: IFilterSection[];
  selectedFilterCount?: number;
  isInlineFilter?: IFilterSection;
}

export interface IOption {
  value: any;
  id?: number;
  key?: string;
  title: string;
  description?: string;
}

export interface IDateConfig {
  hint?: string;
  disabled?: boolean;
  min?: Date | string;
  max?: Date | string;
  value?: Date | string;
  customDateSelectorType: DateSelectorType;
}

export interface IFilterSection {
  id: number;
  title?: string;
  shortTitle?: string;
  options?: IOption[];
  description?: string;
  selectGist?: string;
  defaultTitle?: string;
  propertyName: string;
  // If 'isDeletionDisabled' value is 'true' then this 'defaultValue' property is mandatory,
  // and it should contain the same value what we have in 'preSelectedValue'.
  defaultValue?: any;
  preSelectedValue: any;
  dateConfig?: IDateConfig;
  isDeletionDisabled: boolean;
  parentFilterType: FilterType;
  filterType: FilterSectionType;
  template: TemplateRef<HTMLElement>
  selectFilterType?: SelectDropdownType;
  addNativeOption?: boolean;
  additionalParams?: { [key: string]: any };
}

type DateIntervalRange = { dateFrom: string, dateTo: string };
type DateIntervalResolver = {
  interval: DateIntervals;
  range: () => DateIntervalRange;
  matches?: (title: string) => boolean;
};

export const resolveDateIntervalByTitle = (title: string = ''): IOption | null => {
  const resolver = DATE_INTERVAL_RESOLVERS.find((dateIntervalResolver: DateIntervalResolver): boolean =>
    (dateIntervalResolver.matches || defaultDateIntervalMatches(dateIntervalResolver.interval))(title));

  if (!resolver) {
    return null;
  }

  return {
    title: DATE_INTERVAL_MAPPING[resolver.interval],
    value: resolver.range(),
  };
};

const isDateIntervalTitle = (interval: DateIntervals, title: string): boolean => {
  return title === DATE_INTERVAL_MAPPING[interval] || title === interval;
};

const defaultDateIntervalMatches = (interval: DateIntervals): (title: string) => boolean => {
  return (title: string): boolean => isDateIntervalTitle(interval, title);
};

const dateIntervalTitlePrefix = (interval: DateIntervals): string => DATE_INTERVAL_MAPPING[interval].split(' (')[0];

const DATE_INTERVAL_RESOLVERS: DateIntervalResolver[] = [
  { interval: DateIntervals.TODAY, range: todayDate },
  { interval: DateIntervals.LAST_7_DAYS, range: last7DaysDateRange },
  { interval: DateIntervals.LAST_30_DAYS, range: last30DaysDateRange },
  { interval: DateIntervals.LAST_WEEK, range: lastWeekDateRange },
  { interval: DateIntervals.THIS_WEEK, range: currentWeekDateRange },
  { interval: DateIntervals.LAST_MONTH, range: lastMonthDateRange },
  { interval: DateIntervals.THIS_MONTH, range: currentMonthDateRange },
  {
    interval: DateIntervals.THIS_QUARTER,
    range: currentQuarterDateRange,
    matches: (title: string): boolean => title.startsWith(dateIntervalTitlePrefix(DateIntervals.THIS_QUARTER)),
  },
  {
    interval: DateIntervals.LAST_QUARTER,
    range: lastQuarterDateRange,
    matches: (title: string): boolean => title.startsWith(dateIntervalTitlePrefix(DateIntervals.LAST_QUARTER)),
  },
  {
    interval: DateIntervals.LAST_TWO_QUARTER,
    range: lastTwoQuarterDateRange,
    matches: (title: string): boolean => title.startsWith(dateIntervalTitlePrefix(DateIntervals.LAST_TWO_QUARTER)),
  },
  {
    interval: DateIntervals.THIS_TWO_QUARTER,
    range: currentTwoQuarterDateRange,
    matches: (title: string): boolean => title.startsWith(dateIntervalTitlePrefix(DateIntervals.THIS_TWO_QUARTER)),
  },
  { interval: DateIntervals.LAST_YEAR, range: lastYearDateRange },
  { interval: DateIntervals.THIS_YEAR, range: currentYearDateRange },
  { interval: DateIntervals.ONE_YEAR, range: oneYearDateRange },
  { interval: DateIntervals.TOTAL, range: totalDateRange },
];

export function todayDate(): { dateFrom: string, dateTo: string } {
  const todayStartDate = moment().startOf('D');
  const todayEndDate = moment().startOf('D');

  return formatDateRange(todayStartDate, todayEndDate);
}

export function last7DaysDateRange(): { dateFrom: string, dateTo: string } {
  const last7DaysEndDate = moment().startOf('D');
  const last7DaysStartDate = moment().startOf('D').subtract({ days: 6 });

  return formatDateRange(last7DaysStartDate, last7DaysEndDate);
}

export function last30DaysDateRange(): { dateFrom: string, dateTo: string } {
  const last30DaysEndDate = moment().startOf('D');
  const last30DaysStartDate = moment().startOf('D').subtract({ days: 29 });

  return formatDateRange(last30DaysStartDate, last30DaysEndDate);
}

export function lastWeekDateRange(): { dateFrom: string, dateTo: string } {
  const lastWeekEndDate = moment().subtract({ weeks: 1 }).endOf('week');
  const lastWeekStartDate = moment().subtract({ weeks: 1 }).startOf('week');

  return formatDateRange(lastWeekStartDate, lastWeekEndDate);
}

export function currentWeekDateRange(): { dateFrom: string, dateTo: string } {
  const currentWeekEndDate = moment().endOf('week');
  const currentWeekStartDate = moment().startOf('week');

  return formatDateRange(currentWeekStartDate, currentWeekEndDate);
}

export function lastMonthDateRange(): { dateFrom: string, dateTo: string } {
  const lastMonthEndDate = moment().subtract({ months: 1 }).endOf('month');
  const lastMonthStartDate = moment().subtract({ months: 1 }).startOf('month');

  return formatDateRange(lastMonthStartDate, lastMonthEndDate);
}

export function currentMonthDateRange(): { dateFrom: string, dateTo: string } {
  const currentMonthEndDate = moment().endOf('month');
  const currentMonthStartDate = moment().startOf('month');

  return formatDateRange(currentMonthStartDate, currentMonthEndDate);
}

export function lastQuarterDateRange(): { dateFrom: string, dateTo: string } {
  const quarterAdjustment = (moment().month() % 3) + 1;
  const lastQuarterEndDate = moment().subtract({ months: quarterAdjustment }).endOf('month');
  const lastQuarterStartDate = lastQuarterEndDate.clone().subtract({ months: 2 }).startOf('month');

  return formatDateRange(lastQuarterStartDate, lastQuarterEndDate);
}

export function currentQuarterDateRange(): { dateFrom: string, dateTo: string } {
  const quarterAdjustment = (moment().month() % 3) + 1;
  const currentQuarterEndDate = moment().subtract({ months: quarterAdjustment - 3 }).endOf('month');
  const currentQuarterStartDate = currentQuarterEndDate.clone().subtract({ months: 2 }).startOf('month');

  return formatDateRange(currentQuarterStartDate, currentQuarterEndDate);
}

export function lastTwoQuarterDateRange(): { dateFrom: string, dateTo: string } {
  const quarterAdjustment = (moment().month() % 3) + 1;
  const lastTwoQuarterEndDate = moment().subtract({ months: quarterAdjustment }).endOf('month');
  const lastTwoQuarterStartDate = lastTwoQuarterEndDate.clone().subtract({ months: 2 + 3 }).startOf('month');

  return formatDateRange(lastTwoQuarterStartDate, lastTwoQuarterEndDate);
}

export function currentTwoQuarterDateRange(): { dateFrom: string, dateTo: string } {
  const quarterAdjustment = (moment().month() % 3) + 1;
  const currentTwoQuarterEndDate = moment().subtract({ months: quarterAdjustment - 3 }).endOf('month');
  const currentTwoQuarterStartDate = currentTwoQuarterEndDate.clone().subtract({ months: 2 + 3 }).startOf('month');

  return formatDateRange(currentTwoQuarterStartDate, currentTwoQuarterEndDate);
}

export function lastYearDateRange(): { dateFrom: string, dateTo: string } {
  const lastYearEndDate = moment().subtract({ years: 1 }).endOf('year');
  const lastYearStartDate = moment().subtract({ years: 1 }).startOf('year');

  return formatDateRange(lastYearStartDate, lastYearEndDate);
}

export function currentYearDateRange(): { dateFrom: string, dateTo: string } {
  const currentYearEndDate = moment().endOf('year');
  const currentYearStartDate = moment().startOf('year');

  return formatDateRange(currentYearStartDate, currentYearEndDate);
}

export function oneYearDateRange(): { dateFrom: string, dateTo: string } {
  const oneYearEndDate = moment().startOf('D');
  const oneYearStartDate = moment().startOf('D').subtract({ years: 1 });

  return formatDateRange(oneYearStartDate, oneYearEndDate);
}

export function totalDateRange(): { dateFrom: string, dateTo: string } {
  return formatDateRange('-1', '-1');
}

function formatDateRange(startDate: any, endDate: any): { dateFrom: string, dateTo: string } {
  const appendTime = (value: any, isEnd: boolean): any => {
    // Keep special sentinel values (like '-1') as-is
    if (value === '-1' || value === null || value === undefined || value === '') {
      return value;
    }

    // If we already have a full datetime string, don't touch it
    if (typeof value === 'string' && value.length > 10 && value.includes(':')) {
      return value;
    }

    // At this point we assume we're dealing with a plain date (either Moment or 'YYYY-MM-DD')
    const dateStr = moment.isMoment(value)
      ? moment(value).format('YYYY-MM-DD')
      : value;

    const time = isEnd ? '23:59:59' : '00:00:00';
    return `${dateStr} ${time}`;
  };

  return {
    // Ensure dateFrom always starts at 00:00:00
    dateFrom: appendTime(startDate, false),
    // Ensure dateTo always ends at 23:59:59
    dateTo: appendTime(endDate, true),
  };
}
